import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import LoadingIcon from './LoadingIcon'
import { ChevronLeftIcon, ChevronDownIcon, InfoAlert } from './ui'
import { CancelButton, PrimaryButton, ContinueButton } from './ui'
import { useApi } from '../services/api'
import {
  formatMobileNumber,
  mobileDigitsForComparison,
  normalizeMobileNumber,
  sanitizeRegistrationNumber,
  filterRegistrationInput,
  filterAndTruncateRegistrationInput,
  validateRegistrationNumber,
  validateEmail,
  validateMobileNumber,
  REGISTRATION_DISPLAY_INPUT_MAX_LENGTH,
  REGISTRATION_NUMBER_MAX_LENGTH,
} from '../utils/validation'
import AddressAutocomplete from './AddressAutocomplete'
import OvernightParkingAccordion, { isOvernightParkingUnsaved } from './OvernightParkingAccordion'
import OffRoadRecoveryAccordion, { isOffRoadRecoveryUnsaved } from './OffRoadRecoveryAccordion'
import { UnsavedFieldBadge } from './UnsavedFieldBadge'
import { mapOffRoadRecoveryToOption, OFF_ROAD_RECOVERY_OPTIONS } from './offRoadRecoveryOptions'
import type { PolicyLink, AlterationResult } from '../services/types'
import { getDisplayPremiumFromAlteration, getPremiumChangeDetails, formatPremiumDisplay, nextPaymentStartCopy } from '../utils/alterationResult'
import { isoDateValueToDdMmYyyy } from '../utils/dobDateInput'
import PolicyReferralBlocked from './PolicyReferralBlocked'
import {
  getPolicyReferralMessageFromApiError,
  isPolicyReferralAlterationResponse,
  isPolicyPortalBlockedApiError,
  isPolicyUpdatesBlockedSummary,
  POLICY_REFERRAL_CUSTOMER_MESSAGE,
  PORTAL_POLICY_UPDATES_BLOCKED_MESSAGE,
} from '../utils/policyReferralState'
import { parseWholeDollarCurrencyInput, formatWholeDollarInputValue } from '../utils/currency'
import { computeFieldChanges } from '../utils/policyFieldChanges'
import { policyIsNonPoweredVehicle } from '../utils/policyType'
import { useScrollToTopOnErrorSignal } from '../hooks/useScrollToTopOnErrorSignal'
import { useGoogleMapsScript } from '../hooks/useGoogleMapsScript'

const AUSTRALIAN_STATES = [
  { value: 'NSW', label: 'NSW' },
  { value: 'VIC', label: 'VIC' },
  { value: 'QLD', label: 'QLD' },
  { value: 'WA', label: 'WA' },
  { value: 'SA', label: 'SA' },
  { value: 'TAS', label: 'TAS' },
  { value: 'ACT', label: 'ACT' },
  { value: 'NT', label: 'NT' },
]

const PARKING_LOCATIONS = [
  { value: 'Carport', label: 'Carport' },
  { value: 'Driveway', label: 'Driveway' },
  { value: 'Garage', label: 'Garage' },
  { value: 'Street', label: 'Street' },
  { value: 'Secured parking lot', label: 'Secured parking lot' },
  { value: 'Unsecured parking lot', label: 'Unsecured parking lot' },
  { value: 'Storage', label: 'Storage' },
]

function validateModificationsInput(value: string): string | null {
  const cents = parseWholeDollarCurrencyInput(value)
  if (cents === null || cents < 0) return 'Please enter a valid amount'
  if (cents > 100_000_000) return 'Please enter a value less than or equal to 1,000,000'
  return null
}

/** Whole-dollar AUD display for modifications comparison copy (internal amount is cents). */
function formatModificationsWholeDollars(cents: number): string {
  return (cents / 100).toLocaleString('en-AU', { maximumFractionDigits: 0 })
}

function validateEmailIfPresent(value: string): string | undefined {
  const result = validateEmail(value.trim())
  return result.isValid ? undefined : result.error
}

function validateMobileIfPresent(value: string): string | undefined {
  const result = validateMobileNumber(value.trim())
  return result.isValid ? undefined : result.error
}

/** Bring a field into view after failed submit validation (user may be at the bottom of the form). */
function scrollFieldIntoViewAndFocus(el: HTMLElement | null) {
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  window.requestAnimationFrame(() => {
    if (document.contains(el)) {
      el.focus({ preventScroll: true })
    }
  })
}

function mapOvernightParkingToOption(apiValue: string): string {
  const normalized = apiValue.toLowerCase().trim()
  const aliases: Record<string, string> = {
    garage: 'Garage',
    carport: 'Carport',
    street: 'Street',
    driveway: 'Driveway',
    storage: 'Storage',
    'secured parking lot': 'Secured parking lot',
    'unsecured parking lot': 'Unsecured parking lot',
    gar: 'Garage',
    car: 'Carport',
    str: 'Street',
    drv: 'Driveway',
    sto: 'Storage',
    sec: 'Secured parking lot',
    uns: 'Unsecured parking lot',
  }
  if (aliases[normalized]) {
    return aliases[normalized]
  }
  const found = PARKING_LOCATIONS.find((loc) => loc.value.toLowerCase() === normalized)
  return found?.value ?? apiValue
}


/** Non-collapsible section box (mirrors Policy view structure, no chevron). */
function StaticSection({
  icon,
  title,
  titleBadge,
  children,
}: {
  icon: React.ReactNode
  title: string
  titleBadge?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="bg-background-primary border border-border-secondary border-solid flex flex-col gap-[12px] items-start px-[16px] py-[12px] relative rounded-lg shadow-xs w-full">
      <div className="flex gap-[12px] items-center relative shrink-0 w-full">
        <div className="bg-background-tertiary overflow-clip relative rounded-full shrink-0 size-12">
          <div className="absolute left-3 top-3 overflow-clip size-6 text-text-tertiary-600">
            {icon}
          </div>
        </div>
        <div className="flex-1 flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
          <p className="font-display font-medium text-display-xs leading-[32px] text-text-primary-900 min-w-0">
            {title}
          </p>
          {titleBadge}
        </div>
      </div>
      <div className="flex flex-col items-start relative shrink-0 w-full">
        {children}
      </div>
    </div>
  )
}

export default function UpdatePolicyAllFields() {
  const navigate = useNavigate()
  const { policyId } = useParams<{ policyId: string }>()
  const { getPolicy, updateRegistrationNumber, bindAlteration, abandonAlteration } = useApi()
  const { isLoaded: mapsPlacesLoaded, loadError: mapsPlacesLoadError } = useGoogleMapsScript()
  /** When false, parking field is plain text (script error or not loaded); strict pick-from-list cannot apply. */
  const parkingPlacesSuggestionsAvailable = mapsPlacesLoaded && mapsPlacesLoadError == null
  const stateDropdownRef = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [showStateDropdown, setShowStateDropdown] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitFaultRef, setSubmitFaultRef] = useState<string | null>(null)
  /** When set, show premium-change confirm step (Accept/Cancel). */
  const [alterationResult, setAlterationResult] = useState<AlterationResult | null>(null)
  /** Current (pre-change) term total premium from policy fetch (POLSUM Term), for accept-step comparison. */
  const [currentPremium, setCurrentPremium] = useState<number | null>(null)
  /** Entsia paymentFrequency (e.g. MONTHLY); used with instalment for per-period lines on confirm step. */
  const [currentPaymentFrequency, setCurrentPaymentFrequency] = useState<string | undefined>(undefined)
  /** Per-period amount from billing plan when paying monthly/quarterly. */
  const [currentInstalmentAmount, setCurrentInstalmentAmount] = useState<number | null>(null)
  const [nextInstalmentDueDateFormatted, setNextInstalmentDueDateFormatted] = useState<string | null>(null)
  const [acceptingQuote, setAcceptingQuote] = useState(false)
  const [abandoningQuote, setAbandoningQuote] = useState(false)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [referralMessage, setReferralMessage] = useState<string | null>(null)

  // Vehicle
  const [registrationNumber, setRegistrationNumber] = useState('')
  const [registrationNumberError, setRegistrationNumberError] = useState<string | undefined>(undefined)
  const [registrationNumberTouched, setRegistrationNumberTouched] = useState(false)
  /** True when the last edit would have exceeded max plate length before truncation (paste/extra keystrokes). */
  const [registrationMaxLengthAttempt, setRegistrationMaxLengthAttempt] = useState(false)
  const [state, setState] = useState('')
  const [parkingAddress, setParkingAddress] = useState('')
  /** Most recent parking address that resolved from a Google Place selection. */
  const [verifiedParkingAddress, setVerifiedParkingAddress] = useState<string | null>(null)
  const [parkingAddressError, setParkingAddressError] = useState<string | null>(null)
  const [parkingLocation, setParkingLocation] = useState('')
  const [modificationsValue, setModificationsValue] = useState('')
  const [modificationsInputTouched, setModificationsInputTouched] = useState(false)
  const [modificationsError, setModificationsError] = useState<string | null>(null)
  /** Initial values from policy fetch, for confirm screen From/To */
  const [initialRegistrationNumber, setInitialRegistrationNumber] = useState<string | null>(null)
  const [initialState, setInitialState] = useState<string | null>(null)
  const [initialParkingAddress, setInitialParkingAddress] = useState<string | null>(null)
  const [initialOvernightParking, setInitialOvernightParking] = useState<string | null>(null)
  const [initialModificationsValue, setInitialModificationsValue] = useState<string | null>(null)
  const [initialOffRoadRecovery, setInitialOffRoadRecovery] = useState<string | null>(null)
  const [initialEmail, setInitialEmail] = useState<string | null>(null)
  const [initialMobileNumber, setInitialMobileNumber] = useState<string | null>(null)
  const [initialPostalAddress, setInitialPostalAddress] = useState<string | null>(null)
  // Contact (editable here and in submit; contact-only flow: update-policy-contact route)
  const [email, setEmail] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [emailError, setEmailError] = useState<string | undefined>(undefined)
  const [mobileNumber, setMobileNumber] = useState('')
  const [mobileTouched, setMobileTouched] = useState(false)
  const [mobileError, setMobileError] = useState<string | undefined>(undefined)
  const [postalAddress, setPostalAddress] = useState('')
  /** Most recent postal address that resolved from a Google Place selection. */
  const [verifiedPostalAddress, setVerifiedPostalAddress] = useState<string | null>(null)
  const [postalAddressError, setPostalAddressError] = useState<string | null>(null)

  // Additional cover
  const [offRoadRecovery, setOffRoadRecovery] = useState('1500')
  const [registrationStateApplicable, setRegistrationStateApplicable] = useState(true)

  const userErrorScrollSignal = useMemo(() => {
    const parts: string[] = []
    if (fetchError) parts.push(`fetch:${fetchError}`)
    if (submitError) parts.push(`submit:${submitError}`)
    if (submitFaultRef) parts.push(`fault:${submitFaultRef}`)
    if (confirmError) parts.push(`confirm:${confirmError}`)
    if (registrationNumberError) parts.push(`rego:${registrationNumberError}`)
    if (modificationsError) parts.push(`mod:${modificationsError}`)
    if (parkingAddressError) parts.push(`parking:${parkingAddressError}`)
    if (postalAddressError) parts.push(`postal:${postalAddressError}`)
    return parts.length > 0 ? parts.join('|') : null
  }, [fetchError, submitError, submitFaultRef, confirmError, registrationNumberError, modificationsError, parkingAddressError, postalAddressError])
  useScrollToTopOnErrorSignal(userErrorScrollSignal)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // When we show the accept/decline step, scroll to top so the user sees Approve/Cancel buttons
  useEffect(() => {
    if (alterationResult) {
      window.scrollTo(0, 0)
    }
  }, [alterationResult])

  useEffect(() => {
    let cancelled = false

    const fetchPolicy = async () => {
      if (!policyId) {
        setLoading(false)
        setFetchError('No policy ID provided')
        return
      }
      try {
        setLoading(true)
        setFetchError(null)
        setReferralMessage(null)
        setRegistrationNumberError(undefined)
        setRegistrationNumberTouched(false)
        setModificationsInputTouched(false)
        setModificationsError(null)
        setEmailError(undefined)
        setEmailTouched(false)
        setMobileError(undefined)
        setMobileTouched(false)
        const decoded = decodeURIComponent(policyId)
        const policy = await getPolicy(decoded) as PolicyLink & { mobileNumber?: string }
        if (cancelled) return

        if (policy.referralBlocked === true) {
          setReferralMessage(policy.referralMessage ?? POLICY_REFERRAL_CUSTOMER_MESSAGE)
          return
        }

        if (isPolicyUpdatesBlockedSummary(policy)) {
          setReferralMessage(
            policy.policyUpdatesBlockedMessage?.trim() || PORTAL_POLICY_UPDATES_BLOCKED_MESSAGE
          )
          return
        }

        const showRegistrationState = !policyIsNonPoweredVehicle(policy)
        setRegistrationStateApplicable(showRegistrationState)

        const regoFromPolicy = filterAndTruncateRegistrationInput(policy.registrationNumber ?? '')
        setRegistrationMaxLengthAttempt(false)
        setRegistrationNumber(regoFromPolicy)
        // Align baseline with truncated display so long API values do not read as permanently unsaved.
        setInitialRegistrationNumber(regoFromPolicy || null)
        if (showRegistrationState) {
          const regStateRaw = policy.registrationState?.trim()
          const regState =
            regStateRaw && /^(NSW|VIC|QLD|WA|SA|TAS|ACT|NT)$/i.test(regStateRaw)
              ? regStateRaw.toUpperCase()
              : ''
          if (regState) {
            setState(regState)
            setInitialState(regState)
          } else {
            setState('')
            setInitialState(null)
          }
        } else {
          setState('')
          setInitialState(null)
        }
        setParkingAddress(policy.parkingAddress ?? '')
        setInitialParkingAddress(policy.parkingAddress ?? null)
        setParkingLocation(
          policy.overnightParking ? mapOvernightParkingToOption(policy.overnightParking) : ''
        )
        setInitialOvernightParking(
          policy.overnightParking ? mapOvernightParkingToOption(policy.overnightParking) : null
        )
        setModificationsValue(
          policy.modificationsValue != null ? formatWholeDollarInputValue(String(policy.modificationsValue)) : ''
        )
        setInitialModificationsValue(
          policy.modificationsValue != null ? String(policy.modificationsValue) : null
        )
        setEmail(policy.email ?? '')
        setInitialEmail(policy.email ?? null)
        setMobileNumber(policy.mobileNumber ? formatMobileNumber(policy.mobileNumber) : '')
        setInitialMobileNumber(policy.mobileNumber ?? null)
        setPostalAddress(policy.postalAddress ?? '')
        setInitialPostalAddress(policy.postalAddress ?? null)
        setOffRoadRecovery(
          policy.offRoadRecovery ? mapOffRoadRecoveryToOption(policy.offRoadRecovery) : '1500'
        )
        setInitialOffRoadRecovery(
          policy.offRoadRecovery ? mapOffRoadRecoveryToOption(policy.offRoadRecovery) : null
        )
        if (policy.premium != null && typeof policy.premium === 'number') {
          setCurrentPremium(policy.premium)
        } else {
          setCurrentPremium(null)
        }
        setCurrentPaymentFrequency(
          typeof policy.paymentFrequency === 'string' && policy.paymentFrequency.trim()
            ? policy.paymentFrequency.trim()
            : undefined
        )
        if (policy.instalmentAmount != null && typeof policy.instalmentAmount === 'number') {
          setCurrentInstalmentAmount(policy.instalmentAmount)
        } else {
          setCurrentInstalmentAmount(null)
        }
        const isoDue =
          typeof policy.nextInstalmentDueDate === 'string' && policy.nextInstalmentDueDate.trim()
            ? policy.nextInstalmentDueDate.trim().slice(0, 10)
            : null
        const formattedDue = isoDue ? isoDateValueToDdMmYyyy(isoDue) || null : null
        setNextInstalmentDueDateFormatted(formattedDue)
      } catch (err: unknown) {
        if (cancelled) return
        if (isPolicyPortalBlockedApiError(err)) {
          setReferralMessage(getPolicyReferralMessageFromApiError(err))
          setFetchError(null)
        } else {
          setFetchError(err instanceof Error ? err.message : 'Failed to load policy')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchPolicy()
    return () => {
      cancelled = true
    }
  }, [policyId, getPolicy])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(e.target as Node)) {
        setShowStateDropdown(false)
      }
    }
    if (showStateDropdown) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showStateDropdown])

  const handleBack = () => {
    if (policyId) navigate(`/policy/${encodeURIComponent(policyId)}`)
    else navigate('/dashboard', { replace: true })
  }

  const handleCancel = () => {
    if (alterationResult) {
      setAlterationResult(null)
      return
    }
    // Always exit to policy view — do not use history.back(), or after
    // update-all → add driver → Back (another update-all push), Cancel would land on edit-driver again.
    if (policyId) navigate(`/policy/${encodeURIComponent(policyId)}`)
    else navigate('/dashboard', { replace: true })
  }

  const handleAcceptPremium = async () => {
    if (!policyId || !alterationResult?.quoteReference) return
    setConfirmError(null)
    setAcceptingQuote(true)
    try {
      const decodedPolicyRef = decodeURIComponent(policyId)
      await bindAlteration(decodedPolicyRef, { quoteReference: alterationResult.quoteReference })
      sessionStorage.removeItem('club4x4_policies_cache')
      setAlterationResult(null)
      if (policyId) navigate(`/policy/${encodeURIComponent(policyId)}/update-confirmed`, { replace: true })
      else navigate(-1)
    } catch (err) {
      setConfirmError(err instanceof Error ? err.message : 'Could not accept the quote.')
    } finally {
      setAcceptingQuote(false)
    }
  }

  const handleCancelPremium = async () => {
    if (!policyId || !alterationResult?.quoteReference) {
      setAlterationResult(null)
      return
    }
    setConfirmError(null)
    setAbandoningQuote(true)
    try {
      const decodedPolicyRef = decodeURIComponent(policyId)
      await abandonAlteration(decodedPolicyRef, { quoteReference: alterationResult.quoteReference })
      sessionStorage.removeItem('club4x4_policies_cache')
      setAlterationResult(null)
    } catch (err) {
      setConfirmError(err instanceof Error ? err.message : 'Could not cancel the quote.')
    } finally {
      setAbandoningQuote(false)
    }
  }

  const handleRegistrationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const preTruncate = filterRegistrationInput(e.target.value)
    const exceedsMax =
      sanitizeRegistrationNumber(preTruncate).length > REGISTRATION_NUMBER_MAX_LENGTH
    setRegistrationMaxLengthAttempt(exceedsMax)
    const filtered = filterAndTruncateRegistrationInput(e.target.value)
    setRegistrationNumber(filtered)
    setRegistrationNumberTouched(true)
    const result = validateRegistrationNumber(filtered)
    setRegistrationNumberError(result.isValid ? undefined : result.error)
  }

  const handleModificationsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatWholeDollarInputValue(e.target.value)
    setModificationsValue(formatted)
    if (modificationsInputTouched) {
      setModificationsError(validateModificationsInput(formatted))
    }
  }

  const handleModificationsBlur = () => {
    setModificationsInputTouched(true)
    setModificationsError(validateModificationsInput(modificationsValue))
  }

  /** Enter in single-line inputs would implicitly submit the form; blur instead so only the Update policy button submits. */
  const handleFormEnterKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter') return
    if (e.nativeEvent.isComposing) return
    const target = e.target as HTMLElement
    if (target.tagName === 'TEXTAREA') return
    if (target.tagName === 'BUTTON') return
    if (target.tagName === 'INPUT') {
      const input = target as HTMLInputElement
      const t = input.type
      if (
        t === 'submit' ||
        t === 'button' ||
        t === 'checkbox' ||
        t === 'radio' ||
        t === 'file' ||
        t === 'hidden' ||
        t === 'reset'
      ) {
        return
      }
      e.preventDefault()
      if (document.activeElement === input) {
        input.blur()
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!policyId) return
    const fieldChanges = computeFieldChanges({
      initialRegistrationNumber,
      registrationNumber,
      initialState,
      state,
      initialParkingAddress,
      parkingAddress,
      initialOvernightParking,
      parkingLocation,
      initialModificationsValue,
      modificationsValue,
      initialOffRoadRecovery,
      offRoadRecovery,
      initialEmail,
      email,
      initialMobileNumber,
      mobileNumber,
      initialPostalAddress,
      postalAddress,
      pendingNewListedDriver: null,
      pendingUpdateListedDriver: null,
    })
    const emailErr = validateEmailIfPresent(email)
    const mobileErr = validateMobileIfPresent(mobileNumber)
    setEmailTouched(true)
    setMobileTouched(true)
    if (emailErr) {
      setEmailError(emailErr)
      scrollFieldIntoViewAndFocus(document.getElementById('update-policy-email'))
      return
    }
    if (mobileErr) {
      setMobileError(mobileErr)
      scrollFieldIntoViewAndFocus(document.getElementById('update-policy-mobile'))
      return
    }
    if (!fieldChanges.anyChange) return
    setRegistrationNumberTouched(true)
    const regoResult = validateRegistrationNumber(registrationNumber)
    if (!regoResult.isValid) {
      setRegistrationNumberError(regoResult.error)
      setSubmitError(regoResult.error ?? 'Invalid registration number')
      scrollFieldIntoViewAndFocus(document.getElementById('update-policy-registration'))
      return
    }
    if (registrationStateApplicable && !state) {
      setSubmitError('Please select a state.')
      scrollFieldIntoViewAndFocus(document.getElementById('update-policy-state-select'))
      return
    }
    if (fieldChanges.modChanged) {
      const modsErr = validateModificationsInput(modificationsValue)
      if (modsErr) {
        setModificationsInputTouched(true)
        setModificationsError(modsErr)
        scrollFieldIntoViewAndFocus(document.getElementById('modifications-input'))
        return
      }
    }
    const trimmedParking = parkingAddress.trim()
    const verifiedParkingTrim = (verifiedParkingAddress ?? '').trim()
    const shouldEnforceParkingSuggestionPick =
      fieldChanges.parkingAddrChanged &&
      trimmedParking !== '' &&
      parkingPlacesSuggestionsAvailable &&
      trimmedParking !== verifiedParkingTrim

    if (shouldEnforceParkingSuggestionPick) {
      setParkingAddressError('Please select your parking address from the suggestions list.')
      scrollFieldIntoViewAndFocus(document.getElementById('update-policy-parking-address'))
      return
    }
    setParkingAddressError(null)

    const trimmedPostal = postalAddress.trim()
    const verifiedPostalTrim = (verifiedPostalAddress ?? '').trim()
    const shouldEnforcePostalSuggestionPick =
      fieldChanges.postalChanged &&
      trimmedPostal !== '' &&
      parkingPlacesSuggestionsAvailable &&
      trimmedPostal !== verifiedPostalTrim

    if (shouldEnforcePostalSuggestionPick) {
      setPostalAddressError('Please select your postal address from the suggestions list.')
      scrollFieldIntoViewAndFocus(document.getElementById('update-policy-postal-address'))
      return
    }
    setPostalAddressError(null)

    setRegistrationNumberError(undefined)
    setEmailError(undefined)
    setMobileError(undefined)
    setSubmitError(null)
    setSubmitFaultRef(null)
    setSubmitting(true)
    const decodedPolicyRef = decodeURIComponent(policyId)
    const sanitizedRego = sanitizeRegistrationNumber(registrationNumber)
    const trimmedOvernight = parkingLocation.trim()
    // Only include fields that changed so registration state, parking, and contact/postal do not re-apply together.
    const body = {
      registrationNumber: sanitizedRego,
      ...(registrationStateApplicable && fieldChanges.stateChanged
        ? { state: state || undefined }
        : {}),
      parkingAddress: fieldChanges.parkingAddrChanged && trimmedParking ? trimmedParking : undefined,
      overnightParking: fieldChanges.overnightChanged && trimmedOvernight ? trimmedOvernight : undefined,
      ...(fieldChanges.modChanged && fieldChanges.modTo != null ? { modificationsValue: fieldChanges.modTo } : {}),
      ...(fieldChanges.recoveryChanged ? { offRoadRecovery: offRoadRecovery.trim() || undefined } : {}),
      ...(fieldChanges.emailChanged ? { email: email.trim() || undefined } : {}),
      ...(fieldChanges.mobileChanged ? { mobileNumber: normalizeMobileNumber(mobileNumber) || undefined } : {}),
      ...(fieldChanges.postalChanged ? { postalAddress: trimmedPostal } : {}),
    }
    try {
      const result = await updateRegistrationNumber(decodedPolicyRef, body)
      if (isPolicyReferralAlterationResponse(result)) {
        setReferralMessage(result.message ?? POLICY_REFERRAL_CUSTOMER_MESSAGE)
        setSubmitting(false)
        return
      }
      const acceptable = result.alterationResult?.status === 'ACCEPTABLE'
      if (acceptable && result.alterationResult) {
        // Mount the confirm screen: summarise changes, show premium change, Accept (bind) / Cancel (abandon)
        setAlterationResult(result.alterationResult)
        setSubmitting(false)
        return
      }
      if (policyId) navigate(`/policy/${encodeURIComponent(policyId)}/update-confirmed`, { replace: true })
      else navigate(-1)
    } catch (err: unknown) {
      const e = err as Error & { apiBody?: { message?: string; faultReference?: string } }
      setSubmitError(e?.apiBody?.message ?? e?.message ?? 'Update failed')
      setSubmitFaultRef(e?.apiBody?.faultReference ?? null)
    } finally {
      setSubmitting(false)
    }
  }

  const baselineParsed = parseWholeDollarCurrencyInput(initialModificationsValue ?? '')
  const currentParsed = parseWholeDollarCurrencyInput(modificationsValue)
  const hasModificationsChanged = currentParsed !== baselineParsed
  const modsBaselineCents = baselineParsed ?? 0
  const currentModsValueFormatted = `$${formatModificationsWholeDollars(modsBaselineCents)}`
  const modsEnteredCents = currentParsed
  const modificationsDeltaCents =
    hasModificationsChanged && modsEnteredCents != null ? modsEnteredCents - modsBaselineCents : null
  const modificationsDeltaFormatted =
    modificationsDeltaCents != null && modificationsDeltaCents !== 0
      ? `${modificationsDeltaCents > 0 ? '+' : '-'}$${formatModificationsWholeDollars(Math.abs(modificationsDeltaCents))}`
      : null
  const overnightParkingUnsaved = isOvernightParkingUnsaved(parkingLocation, initialOvernightParking)
  const registrationUnsaved =
    sanitizeRegistrationNumber(registrationNumber) !==
    sanitizeRegistrationNumber(initialRegistrationNumber ?? '')
  const stateUnsaved = (state?.trim() ?? '') !== (initialState ?? '')
  const parkingAddressUnsaved =
    (parkingAddress?.trim() ?? '') !== (initialParkingAddress?.trim() ?? '')
  const emailUnsaved = (email?.trim() ?? '') !== (initialEmail?.trim() ?? '')
  const mobileUnsaved =
    mobileDigitsForComparison(mobileNumber) !== mobileDigitsForComparison(initialMobileNumber ?? '')
  const postalUnsaved = (postalAddress?.trim() ?? '') !== (initialPostalAddress?.trim() ?? '')
  const offRoadUnsaved = isOffRoadRecoveryUnsaved(offRoadRecovery, initialOffRoadRecovery)

  const policyUpdateFieldChanges = computeFieldChanges({
    initialRegistrationNumber,
    registrationNumber,
    initialState,
    state,
    initialParkingAddress,
    parkingAddress,
    initialOvernightParking,
    parkingLocation,
    initialModificationsValue,
    modificationsValue,
    initialOffRoadRecovery,
    offRoadRecovery,
    initialEmail,
    email,
    initialMobileNumber,
    mobileNumber,
    initialPostalAddress,
    postalAddress,
    pendingNewListedDriver: null,
    pendingUpdateListedDriver: null,
  })
  const canSubmitPolicyUpdate = policyUpdateFieldChanges.anyChange

  if (referralMessage) {
    return (
      <PolicyReferralBlocked
        message={referralMessage}
        onBackToDashboard={() => navigate('/dashboard')}
      />
    )
  }

  return (
    <div className="bg-background-brand-primary flex flex-col items-start relative min-h-screen w-full">
      <DashboardNavigation />

      {/* Header - confirm step vs form */}
      <div className="bg-background-brand-solid flex flex-col gap-0 items-center overflow-clip px-0 py-[32px] relative shrink-0 w-full">
        <div className="flex flex-col gap-0 items-center max-w-container-desktop px-[16px] md:px-[32px] py-0 relative shrink-0 w-full">
          <div className="flex flex-col gap-[32px] items-start relative shrink-0 w-full">
            {!alterationResult && (
              <button
                onClick={handleBack}
                className="cursor-pointer flex gap-[4px] items-center justify-center overflow-clip p-0 relative shrink-0"
              >
                <div className="overflow-clip relative shrink-0 size-5 text-text-primary-on-brand">
                  <ChevronLeftIcon />
                </div>
                <p className="font-body font-semibold text-sm leading-sm text-text-primary-on-brand">
                  Back
                </p>
              </button>
            )}
            <div className="flex flex-col gap-[12px] items-start relative shrink-0 w-full">
              <p className="font-display font-semibold text-display-sm leading-[38px] text-text-primary-on-brand w-full">
                {alterationResult ? 'Confirm your change' : 'Update policy'}
              </p>
              <p className="font-body font-regular text-sm leading-sm text-text-primary-on-brand">
                {alterationResult
                  ? 'Review the proposed changes below. Approve to apply them, or Back to keep your current details.'
                  : 'Change any of the fields below, then submit once to update the policy.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main id="main" className="flex flex-col gap-[20px] items-start px-[16px] md:px-[32px] py-[32px] relative shrink-0 w-full max-w-container-desktop mx-auto">
        {loading && (
          <div className="bg-background-primary border border-border-primary border-solid flex flex-col items-center justify-center px-[20px] py-[48px] relative rounded-2xl shadow-xs shrink-0 w-full max-w-[768px]">
            <LoadingIcon size="medium" message="Loading policy details..." />
          </div>
        )}

        {!loading && fetchError && (
          <div className="bg-background-primary border border-red-300 border-solid flex flex-col items-center justify-center px-[20px] py-[24px] relative rounded-2xl shadow-xs shrink-0 w-full max-w-[768px]">
            <p className="font-body font-semibold text-md leading-md text-red-600">
              Unable to load policy
            </p>
            <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600 mt-2">
              {fetchError}
            </p>
            <button
              onClick={handleBack}
              className="mt-4 px-4 py-2 bg-background-secondary rounded-md font-body font-medium text-sm text-text-primary-900 hover:bg-background-secondary-hover"
            >
              Go back
            </button>
          </div>
        )}

        {!loading && !fetchError && submitError && !alterationResult && (
          <div className="max-w-[768px] w-full rounded-lg bg-red-50 border border-red-200 px-4 py-3" role="alert">
            <p className="font-body font-medium text-sm text-red-800">{submitError}</p>
            {submitFaultRef && (
              <p className="font-body font-regular text-xs text-red-700 mt-1">Reference: {submitFaultRef}</p>
            )}
          </div>
        )}

        {/* Premium change confirm step (after modify returned ACCEPTABLE) */}
        {!loading && !fetchError && alterationResult && (
          <>
            {(() => {
              const newTermAmount = getDisplayPremiumFromAlteration(alterationResult)
              const details = getPremiumChangeDetails({
                oldTermPremium: currentPremium,
                newTermPremium: newTermAmount,
                oldInstalment: currentInstalmentAmount,
                paymentFrequencyRaw: currentPaymentFrequency,
              })
              const fc = computeFieldChanges({
                initialRegistrationNumber,
                registrationNumber,
                initialState,
                state,
                initialParkingAddress,
                parkingAddress,
                initialOvernightParking,
                parkingLocation,
                initialModificationsValue,
                modificationsValue,
                initialOffRoadRecovery,
                offRoadRecovery,
                initialEmail,
                email,
                initialMobileNumber,
                mobileNumber,
                initialPostalAddress,
                postalAddress,
                pendingNewListedDriver: null,
                pendingUpdateListedDriver: null,
              })
              return (
                <div className="flex flex-col gap-[24px] w-full max-w-[768px]">
                  {/* Premium heading */}
                  <h2 className="font-display font-semibold text-display-xs leading-[30px] text-text-primary-900">
                    {details.unchanged
                      ? 'Your premium remains unchanged'
                      : `Your premium will ${details.direction}`}
                  </h2>

                  {/* Premium card */}
                  {!details.unchanged && details.newAmount != null && (
                    <div className="border border-border-primary border-solid rounded-xl px-[20px] py-[20px]">
                      <p className="font-body font-semibold text-sm leading-sm text-text-primary-900">
                        Your new {details.frequencyLabel} premium
                      </p>
                      <p className="font-display font-semibold text-display-sm leading-[38px] text-text-primary-900 mt-[4px]">
                        {formatPremiumDisplay(details.newAmount)}
                      </p>
                      <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600 mt-[4px]">
                        Including GST and government charges
                      </p>
                    </div>
                  )}

                  {/* Explanation text */}
                  {!details.unchanged && details.difference != null && (
                    <div className="flex flex-col gap-[12px]">
                      <p className="font-body font-regular text-sm leading-sm text-text-secondary-700">
                        If you make this update, your {details.frequencyLabel} payments will {details.direction} by{' '}
                        <span className="font-semibold">{formatPremiumDisplay(details.difference)}</span> per {details.periodLabel}.
                      </p>
                      <p className="font-body font-regular text-sm leading-sm text-text-secondary-700">
                        {nextPaymentStartCopy(details.frequencyLabel, nextInstalmentDueDateFormatted)}
                      </p>
                    </div>
                  )}

                  {details.unchanged && (
                    <p className="font-body font-regular text-sm leading-sm text-text-secondary-700">
                      The changes you've made do not affect your premium.
                    </p>
                  )}

                  {/* Proposed changes */}
                  <div className="border border-border-primary border-solid rounded-xl px-[20px] py-[20px] flex flex-col gap-[16px]">
                    <p className="font-body font-semibold text-sm leading-sm text-text-primary-900">
                      Proposed changes
                    </p>
                    <div className="flex flex-col gap-3 w-full">
                      {!fc.anyChange ? (
                        <p className="font-body font-regular text-sm leading-sm text-text-secondary-600">
                          No field values were changed.
                        </p>
                      ) : (
                        <>
                          {fc.regoChanged && (
                            <p className="font-body font-regular text-sm leading-sm text-text-primary-900">
                              <span className="font-medium text-text-secondary-700">Registration number</span>
                              <br />
                              <span className="text-text-secondary-600">From: {fc.regoFrom || '—'}</span>
                              <br />
                              <span className="text-text-primary-900">To: {fc.regoTo || '—'}</span>
                              {registrationStateApplicable && fc.stateTo ? ` (${fc.stateTo})` : ''}
                            </p>
                          )}
                          {registrationStateApplicable && fc.stateChanged && (
                            <p className="font-body font-regular text-sm leading-sm text-text-primary-900">
                              <span className="font-medium text-text-secondary-700">State of registration</span>
                              <br />
                              <span className="text-text-secondary-600">From: {fc.stateFrom || '—'}</span>
                              <br />
                              <span className="text-text-primary-900">To: {fc.stateTo || '—'}</span>
                            </p>
                          )}
                          {fc.parkingAddrChanged && (
                            <p className="font-body font-regular text-sm leading-sm text-text-primary-900">
                              <span className="font-medium text-text-secondary-700">Parking address</span>
                              <br />
                              <span className="text-text-secondary-600">From: {fc.parkingAddrFrom || '—'}</span>
                              <br />
                              <span className="text-text-primary-900">To: {fc.parkingAddrTo || '—'}</span>
                            </p>
                          )}
                          {fc.overnightChanged && (
                            <p className="font-body font-regular text-sm leading-sm text-text-primary-900">
                              <span className="font-medium text-text-secondary-700">Overnight parking</span>
                              <br />
                              <span className="text-text-secondary-600">From: {fc.overnightFrom || '—'}</span>
                              <br />
                              <span className="text-text-primary-900">To: {fc.overnightTo || '—'}</span>
                            </p>
                          )}
                          {fc.modChanged && (
                            <p className="font-body font-regular text-sm leading-sm text-text-primary-900">
                              <span className="font-medium text-text-secondary-700">Modifications and accessories</span>
                              <br />
                              <span className="text-text-secondary-600">From: {fc.modFrom != null ? `$${Number(fc.modFrom).toLocaleString('en-AU', { maximumFractionDigits: 0 })}` : '—'}</span>
                              <br />
                              <span className="text-text-primary-900">To: {fc.modTo != null ? `$${Number(fc.modTo).toLocaleString('en-AU', { maximumFractionDigits: 0 })}` : '—'}</span>
                            </p>
                          )}
                          {fc.recoveryChanged && (
                            <p className="font-body font-regular text-sm leading-sm text-text-primary-900">
                              <span className="font-medium text-text-secondary-700">Off-road recovery cover</span>
                              <br />
                              <span className="text-text-secondary-600">From: {fc.recoveryFrom ? OFF_ROAD_RECOVERY_OPTIONS.find((o) => o.value === fc.recoveryFrom)?.label ?? `$${Number(fc.recoveryFrom).toLocaleString()}` : '—'}</span>
                              <br />
                              <span className="text-text-primary-900">To: {fc.recoveryTo ? OFF_ROAD_RECOVERY_OPTIONS.find((o) => o.value === fc.recoveryTo)?.label ?? `$${Number(fc.recoveryTo).toLocaleString()}` : '—'}</span>
                            </p>
                          )}
                          {fc.emailChanged && (
                            <p className="font-body font-regular text-sm leading-sm text-text-primary-900">
                              <span className="font-medium text-text-secondary-700">Email</span>
                              <br />
                              <span className="text-text-secondary-600">From: {fc.emailFrom || '—'}</span>
                              <br />
                              <span className="text-text-primary-900">To: {fc.emailTo || '—'}</span>
                            </p>
                          )}
                          {fc.mobileChanged && (
                            <p className="font-body font-regular text-sm leading-sm text-text-primary-900">
                              <span className="font-medium text-text-secondary-700">Mobile number</span>
                              <br />
                              <span className="text-text-secondary-600">From: {initialMobileNumber ? formatMobileNumber(initialMobileNumber) : '—'}</span>
                              <br />
                              <span className="text-text-primary-900">To: {mobileNumber?.trim() ? formatMobileNumber(mobileNumber.trim()) : '—'}</span>
                            </p>
                          )}
                          {fc.postalChanged && (
                            <p className="font-body font-regular text-sm leading-sm text-text-primary-900">
                              <span className="font-medium text-text-secondary-700">Postal address</span>
                              <br />
                              <span className="text-text-secondary-600">From: {fc.postalFrom || '—'}</span>
                              <br />
                              <span className="text-text-primary-900">To: {fc.postalTo || '—'}</span>
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Tax invoice info */}
                  <p className="font-body font-regular text-sm leading-sm text-text-secondary-700">
                    Tax invoices for policy alterations are available upon request. Contact us on{' '}
                    <a href="tel:1300296296" className="underline text-text-brand-secondary-700 whitespace-nowrap">
                      1300 296 296
                    </a>{' '}
                    if you require one.
                  </p>

                  {confirmError && (
                    <p className="font-body font-regular text-sm leading-sm text-red-600">{confirmError}</p>
                  )}

                </div>
              )
            })()}
            <div className="flex items-center justify-between max-w-[768px] pt-[12px] relative shrink-0 w-full">
              <button
                onClick={handleCancelPremium}
                disabled={acceptingQuote || abandoningQuote}
                className="bg-background-primary border border-border-primary border-solid flex gap-[4px] items-center justify-center overflow-clip px-[12px] py-[8px] relative rounded-md shadow-xs shrink-0 disabled:opacity-50"
              >
                <div className="overflow-clip relative shrink-0 size-5 text-text-tertiary-600">
                  <ChevronLeftIcon />
                </div>
                <span className="font-body font-semibold text-sm leading-sm text-text-secondary-700">
                  {abandoningQuote ? 'Going back…' : 'Back'}
                </span>
              </button>
              <ContinueButton onClick={handleAcceptPremium} disabled={acceptingQuote || abandoningQuote}>
                {acceptingQuote ? 'Approving…' : 'Continue'}
              </ContinueButton>
            </div>
          </>
        )}

        {!loading && !fetchError && !alterationResult && (
          <>
            {/* Mobile: Warning box above form */}
            <div className="lg:hidden w-full">
              <InfoAlert
                message="Making changes to your policy may affect your premium"
                description="Some policy changes cannot be made online. To make changes that are not listed here, call us on 1300 296 296."
              />
            </div>

            {/* Desktop: Side by side layout */}
            <div className="flex flex-col lg:flex-row gap-[20px] items-start w-full">
            <form
              id="update-policy-all-fields-form"
              onSubmit={handleSubmit}
              onKeyDown={handleFormEnterKeyDown}
              className="flex flex-col gap-[32px] items-start w-full lg:flex-1 md:max-w-[768px]"
            >
            {/* Top actions: same as bottom so Update policy is visible without scrolling */}
            <div className="flex flex-wrap gap-[12px] items-center w-full pb-4 border-b border-border-primary">
              <CancelButton type="button" onClick={handleCancel}>
                Cancel
              </CancelButton>
              <PrimaryButton
                type="submit"
                disabled={submitting || !canSubmitPolicyUpdate}
                title={
                  !submitting && !canSubmitPolicyUpdate
                    ? 'Change something on the form before you can update your policy'
                    : undefined
                }
              >
                {submitting ? 'Submitting...' : 'Update policy'}
              </PrimaryButton>
            </div>
            {/* Vehicle – mirrors Policy view section order and icons */}
            <StaticSection
              icon={<img src="/Vehicle-Icon.svg" alt="" className="w-6 h-6" />}
              title="Vehicle"
            >
              <fieldset className="flex flex-col gap-[16px] items-start w-full border-0 p-0 m-0">
                <div className="flex flex-col gap-[12px] w-full">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <label
                    className="font-body font-regular text-sm leading-sm text-text-tertiary-600"
                    htmlFor="update-policy-registration"
                  >
                    Registration number
                  </label>
                  {registrationUnsaved && (
                    <UnsavedFieldBadge describedById="reg-unsaved-desc" />
                  )}
                </div>
                <input
                  id="update-policy-registration"
                  type="text"
                  value={registrationNumber}
                  onChange={handleRegistrationChange}
                  onBlur={() => {
                    setRegistrationNumberTouched(true)
                    setRegistrationMaxLengthAttempt(false)
                    const result = validateRegistrationNumber(registrationNumber)
                    setRegistrationNumberError(result.isValid ? undefined : result.error)
                  }}
                  maxLength={REGISTRATION_DISPLAY_INPUT_MAX_LENGTH}
                  autoComplete="off"
                  className={`bg-background-primary border border-solid px-[12px] py-[10px] rounded-lg w-full font-body text-md text-text-primary-900 uppercase focus:outline-none focus:ring-2 ${
                    registrationMaxLengthAttempt ||
                    (registrationNumberTouched && registrationNumberError)
                      ? 'border-red-500 focus:ring-red-500'
                      : registrationUnsaved
                        ? 'border-yellow-500 focus:ring-yellow-500/80'
                        : 'border-border-primary focus:ring-brand-border'
                  }`}
                  placeholder="e.g. ABC 123"
                  aria-invalid={
                    registrationMaxLengthAttempt ||
                    !!(registrationNumberTouched && registrationNumberError)
                  }
                  aria-describedby={
                    [
                      registrationUnsaved ? 'reg-unsaved-desc' : '',
                      registrationNumberTouched && registrationNumberError ? 'rego-error' : '',
                      registrationMaxLengthAttempt ? 'rego-max-length-hint' : '',
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                />
                {registrationNumberTouched && registrationNumberError && (
                  <p id="rego-error" className="font-body font-regular text-sm leading-sm text-red-500">
                    {registrationNumberError}
                  </p>
                )}
                {registrationMaxLengthAttempt && (
                  <p id="rego-max-length-hint" className="font-body font-regular text-sm leading-sm text-red-500">
                    Registration number cannot exceed {REGISTRATION_NUMBER_MAX_LENGTH} characters.
                  </p>
                )}
              </div>
              {registrationStateApplicable && (
              <div className="flex flex-col gap-[12px] w-full" ref={stateDropdownRef}>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="font-body font-regular text-sm leading-sm text-text-tertiary-600">
                    State
                  </span>
                  {stateUnsaved && <UnsavedFieldBadge describedById="state-unsaved-desc" />}
                </div>
                <button
                  id="update-policy-state-select"
                  type="button"
                  onClick={() => setShowStateDropdown(!showStateDropdown)}
                  className={
                    stateUnsaved
                      ? 'bg-background-primary border border-yellow-500 flex gap-[8px] items-center justify-between px-[12px] py-[10px] rounded-lg w-full font-body text-md text-text-primary-900 text-left focus:outline-none focus:ring-2 focus:ring-yellow-500/80'
                      : 'bg-background-primary border border-border-primary border-solid flex gap-[8px] items-center justify-between px-[12px] py-[10px] rounded-lg w-full font-body text-md text-text-primary-900 text-left focus:outline-none focus:ring-2 focus:ring-brand-border'
                  }
                  aria-describedby={stateUnsaved ? 'state-unsaved-desc' : undefined}
                >
                  <span>{state || 'Select state'}</span>
                  <ChevronDownIcon />
                </button>
                {showStateDropdown && (
                  <div className="border border-border-primary border-solid rounded-lg overflow-hidden bg-background-primary shadow-xs">
                    {AUSTRALIAN_STATES.map((s) => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => {
                          setState(s.value)
                          setShowStateDropdown(false)
                        }}
                        className="block w-full px-[12px] py-[10px] text-left font-body text-md text-text-primary-900 hover:bg-background-secondary-hover"
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              )}
              <div className="flex flex-col gap-[12px] w-full">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <label className="font-body font-regular text-sm leading-sm text-text-tertiary-600">
                    Parking address
                  </label>
                  {parkingAddressUnsaved && <UnsavedFieldBadge describedById="parking-unsaved-desc" />}
                </div>
                <p className="font-body font-regular text-xs text-text-tertiary-600 -mt-2">
                  {registrationStateApplicable
                    ? 'Use the full street address. The state here can differ from your registration state.'
                    : 'Use the full street address.'}
                </p>
                <div
                  className={
                    parkingAddressUnsaved
                      ? 'bg-background-primary border border-yellow-500 px-[12px] py-[10px] rounded-lg w-full focus-within:ring-2 focus-within:ring-yellow-500/80'
                      : 'bg-background-primary border border-border-primary border-solid px-[12px] py-[10px] rounded-lg w-full focus-within:ring-2 focus-within:ring-brand-border'
                  }
                >
                  <AddressAutocomplete
                    id="update-policy-parking-address"
                    value={parkingAddress}
                    onChange={(v) => {
                      setParkingAddress(v)
                      if (parkingAddressError) setParkingAddressError(null)
                    }}
                    onAddressVerified={(v) => {
                      setVerifiedParkingAddress(v)
                      if (parkingAddressError) setParkingAddressError(null)
                    }}
                    placeholder="Street address"
                    ariaInvalid={!!parkingAddressError}
                    ariaDescribedBy={parkingAddressError ? 'parking-address-error' : undefined}
                  />
                </div>
                {parkingAddressError && (
                  <p
                    id="parking-address-error"
                    className="font-body font-regular text-sm leading-sm text-red-500"
                  >
                    {parkingAddressError}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-[12px] w-full">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <label
                    htmlFor="update-policy-overnight-parking"
                    className="font-body font-regular text-sm leading-sm text-text-tertiary-600"
                    aria-describedby={overnightParkingUnsaved ? 'overnight-parking-unsaved-desc' : undefined}
                  >
                    Overnight parking location
                  </label>
                  {overnightParkingUnsaved && (
                    <UnsavedFieldBadge describedById="overnight-parking-unsaved-desc" />
                  )}
                </div>
                <OvernightParkingAccordion
                  id="update-policy-overnight-parking"
                  value={parkingLocation}
                  onChange={setParkingLocation}
                  baselineValue={initialOvernightParking}
                />
              </div>
              <div className="flex flex-col gap-[6px] w-full">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <label
                    htmlFor="modifications-input"
                    className="font-body font-regular text-sm leading-sm text-text-tertiary-600"
                  >
                    Modifications and accessories value
                  </label>
                  {hasModificationsChanged && (
                    <UnsavedFieldBadge describedById="modifications-unsaved-desc" />
                  )}
                </div>
                <div
                  className={`bg-background-primary border border-solid flex gap-[8px] items-center px-[12px] py-[10px] relative rounded-lg shadow-xs w-full focus-within:ring-2 ${
                    modificationsInputTouched && modificationsError
                      ? 'border-red-500 focus-within:ring-red-500'
                      : hasModificationsChanged
                        ? 'border-yellow-500 focus-within:ring-yellow-500/80'
                        : 'border-border-primary focus-within:ring-brand-border'
                  }`}
                >
                  <span className="font-body font-regular text-md leading-md text-text-quaternary-500">$</span>
                  <input
                    id="modifications-input"
                    type="text"
                    inputMode="numeric"
                    value={modificationsValue}
                    onChange={handleModificationsChange}
                    onBlur={handleModificationsBlur}
                    aria-invalid={modificationsInputTouched && !!modificationsError}
                    aria-describedby={[
                      hasModificationsChanged ? 'modifications-unsaved-desc' : '',
                      modificationsInputTouched && modificationsError ? 'modifications-error' : '',
                      hasModificationsChanged ? 'modifications-current-value' : '',
                      'modifications-help',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    className="flex-1 font-body font-regular text-md leading-md text-text-primary-900 outline-none border-none bg-transparent"
                    placeholder="0"
                  />
                </div>
                {modificationsInputTouched && modificationsError && (
                  <p id="modifications-error" className="font-body font-regular text-sm leading-sm text-red-500">{modificationsError}</p>
                )}
                {hasModificationsChanged && (
                  <p
                    id="modifications-current-value"
                    className="font-body font-regular text-sm text-text-tertiary-600 tabular-nums"
                  >
                    <span className="text-text-tertiary-600">On your policy:</span>{' '}
                    <span className="font-medium text-text-secondary-700">{currentModsValueFormatted}</span>
                    {modificationsDeltaFormatted != null ? (
                      <>
                        {' '}
                        <span className="text-text-quaternary-500" aria-hidden>
                          ·
                        </span>
                        {' '}
                        <span className="text-text-tertiary-600">Change:</span>{' '}
                        <span className="font-medium text-text-secondary-700">{modificationsDeltaFormatted}</span>
                      </>
                    ) : null}
                  </p>
                )}
                <p id="modifications-help" className="font-body font-regular text-sm text-text-tertiary-600">
                  Keep invoices and certifications for your vehicle modifications. You&apos;ll need them if you make a
                  claim.
                </p>
              </div>
              </fieldset>
            </StaticSection>

            {/* Contact details */}
            <StaticSection
              icon={<img src="/Contact-details-Icon.svg" alt="" className="w-6 h-6" />}
              title="Contact details"
            >
              <fieldset className="flex flex-col gap-[16px] items-start w-full border-0 p-0 m-0">
              <div className="flex flex-col gap-[12px] w-full">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <label
                    className="font-body font-regular text-sm leading-sm text-text-tertiary-600"
                    htmlFor="update-policy-email"
                  >
                    Email
                  </label>
                  {emailUnsaved && <UnsavedFieldBadge describedById="email-unsaved-desc" />}
                </div>
                <input
                  id="update-policy-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  spellCheck={false}
                  maxLength={254}
                  value={email}
                  onChange={(e) => {
                    const value = e.target.value
                    setEmail(value)
                    if (emailTouched) setEmailError(validateEmailIfPresent(value))
                  }}
                  onBlur={() => {
                    setEmailTouched(true)
                    setEmailError(validateEmailIfPresent(email))
                  }}
                  className={`bg-background-primary border border-solid px-[12px] py-[10px] rounded-lg w-full font-body text-md text-text-primary-900 focus:outline-none focus:ring-2 ${
                    emailTouched && emailError
                      ? 'border-red-500 focus:ring-red-500'
                      : emailUnsaved
                        ? 'border-yellow-500 focus:ring-yellow-500/80'
                        : 'border-border-primary focus:ring-brand-border'
                  }`}
                  placeholder="email@example.com"
                  aria-invalid={!!(emailTouched && emailError)}
                  aria-describedby={
                    [
                      emailUnsaved ? 'email-unsaved-desc' : '',
                      emailTouched && emailError ? 'email-error' : '',
                      'email-helper',
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                />
                {emailTouched && emailError && (
                  <p id="email-error" className="font-body font-regular text-sm leading-sm text-red-500">
                    {emailError}
                  </p>
                )}
                <p id="email-helper" className="font-body font-regular text-sm text-text-tertiary-600">
                  This is where we'll send your policy updates and renewal
                </p>
              </div>
              <div className="flex flex-col gap-[12px] w-full">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <label
                    className="font-body font-regular text-sm leading-sm text-text-tertiary-600"
                    htmlFor="update-policy-mobile"
                  >
                    Mobile number
                  </label>
                  {mobileUnsaved && <UnsavedFieldBadge describedById="mobile-unsaved-desc" />}
                </div>
                <input
                  id="update-policy-mobile"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={20}
                  value={mobileNumber}
                  onChange={(e) => {
                    const value = e.target.value
                    setMobileNumber(value)
                    if (mobileTouched) setMobileError(validateMobileIfPresent(value))
                  }}
                  onBlur={() => {
                    setMobileTouched(true)
                    setMobileError(validateMobileIfPresent(mobileNumber))
                  }}
                  className={`bg-background-primary border border-solid px-[12px] py-[10px] rounded-lg w-full font-body text-md text-text-primary-900 focus:outline-none focus:ring-2 ${
                    mobileTouched && mobileError
                      ? 'border-red-500 focus:ring-red-500'
                      : mobileUnsaved
                        ? 'border-yellow-500 focus:ring-yellow-500/80'
                        : 'border-border-primary focus:ring-brand-border'
                  }`}
                  placeholder="04XX XXX XXX"
                  aria-invalid={!!(mobileTouched && mobileError)}
                  aria-describedby={
                    [
                      mobileUnsaved ? 'mobile-unsaved-desc' : '',
                      mobileTouched && mobileError ? 'mobile-error' : '',
                      'mobile-helper',
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                />
                {mobileTouched && mobileError && (
                  <p id="mobile-error" className="font-body font-regular text-sm leading-sm text-red-500">
                    {mobileError}
                  </p>
                )}
                <p id="mobile-helper" className="font-body font-regular text-sm text-text-tertiary-600">
                  We'll call you on this number
                </p>
              </div>
              <div className="flex flex-col gap-[12px] w-full">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <label className="font-body font-regular text-sm leading-sm text-text-tertiary-600">
                    Postal address
                  </label>
                  {postalUnsaved && <UnsavedFieldBadge describedById="postal-unsaved-desc" />}
                </div>
                <div
                  className={
                    postalUnsaved
                      ? 'bg-background-primary border border-yellow-500 px-[12px] py-[10px] rounded-lg w-full focus-within:ring-2 focus-within:ring-yellow-500/80'
                      : 'bg-background-primary border border-border-primary border-solid px-[12px] py-[10px] rounded-lg w-full focus-within:ring-2 focus-within:ring-brand-border'
                  }
                >
                  <AddressAutocomplete
                    id="update-policy-postal-address"
                    value={postalAddress}
                    onChange={(v) => {
                      setPostalAddress(v)
                      if (postalAddressError) setPostalAddressError(null)
                    }}
                    onAddressVerified={(v) => {
                      setVerifiedPostalAddress(v)
                      if (postalAddressError) setPostalAddressError(null)
                    }}
                    placeholder="Full postal address"
                    ariaInvalid={!!postalAddressError}
                    ariaDescribedBy={postalAddressError ? 'postal-address-error' : undefined}
                  />
                </div>
                {postalAddressError && (
                  <p
                    id="postal-address-error"
                    className="font-body font-regular text-sm leading-sm text-red-500"
                  >
                    {postalAddressError}
                  </p>
                )}
              </div>
              </fieldset>
            </StaticSection>

            {/* Policy extras */}
            <StaticSection
              icon={<img src="/Policy-extras.svg" alt="" className="w-6 h-6" />}
              title="Policy extras"
            >
              <fieldset className="flex flex-col gap-[16px] items-start w-full border-0 p-0 m-0">
                <legend className="sr-only">Off-road recovery cover amount</legend>
                <div className="flex flex-col gap-[12px] w-full">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="font-body font-regular text-sm leading-sm text-text-tertiary-600">
                      Off-road recovery cover
                    </span>
                    {offRoadUnsaved && <UnsavedFieldBadge describedById="offroad-unsaved-desc" />}
                  </div>
                  {registrationStateApplicable ? (
                    <p className="font-body font-regular text-sm text-text-tertiary-600">
                      If you get stuck beyond 2WD-accessible roads, your policy will cover you for $1,500 of the
                      recovery cost. However, you can choose to increase your off-road recovery amount, for
                      extra cover.
                    </p>
                  ) : (
                    <p className="font-body font-regular text-sm text-text-tertiary-600">
                      Off-road recovery cover protects your caravan or camper trailer when specialist recovery
                      is needed in remote or difficult-access areas. Choose the cover level that suits your needs.
                    </p>
                  )}
                  <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600">
                    <a
                      href="https://www.club4x4.com.au/off-road-recovery/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-background-brand-solid underline underline-offset-2"
                    >
                      Read more about off-road recovery cover here
                    </a>
                  </p>
                  <OffRoadRecoveryAccordion
                    id="update-policy-offroad"
                    value={offRoadRecovery}
                    onChange={setOffRoadRecovery}
                    baselineValue={initialOffRoadRecovery}
                  />
                </div>
              </fieldset>
            </StaticSection>

            {/* Actions */}
            <div className="flex flex-wrap gap-[12px] items-center w-full pt-4 border-t border-border-primary">
              <CancelButton onClick={handleCancel}>Cancel</CancelButton>
              <PrimaryButton
                type="submit"
                disabled={submitting || !canSubmitPolicyUpdate}
                title={
                  !submitting && !canSubmitPolicyUpdate
                    ? 'Change something on the form before you can update your policy'
                    : undefined
                }
              >
                {submitting ? 'Submitting...' : 'Update policy'}
              </PrimaryButton>
            </div>
          </form>

              {/* Desktop: Warning box to the right */}
              <aside className="hidden lg:block shrink-0 w-full lg:max-w-[280px]">
                <InfoAlert
                  message="Making changes to your policy may affect your premium"
                  description="Some policy changes cannot be made online. To make changes that are not listed here, call us on 1300 296 296."
                />
              </aside>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  )
}
